<template>
  <div class="notif-bell" ref="bellRef">
    <button class="bell-btn" @click="toggleOpen" :aria-label="`${unreadCount} notifications`">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2a6 6 0 00-6 6v3l-1.5 2.5A1 1 0 003.4 15h13.2a1 1 0 00.9-1.5L16 11V8a6 6 0 00-6-6z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
        <path d="M8 15a2 2 0 004 0" stroke="currentColor" stroke-width="1.6"/>
      </svg>
      <span v-if="unreadCount > 0" class="badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
    </button>

    <!-- Dropdown -->
    <Transition name="notif-drop">
      <div v-if="open" class="notif-dropdown">
        <div class="notif-header">
          <span>Notifications</span>
          <button v-if="unreadCount > 0" class="mark-all-btn" @click="markAllRead">Mark all read</button>
        </div>

        <div v-if="loading" class="notif-loading">
          <div class="spinner" style="width:18px;height:18px;border-width:2px;margin:0 auto" />
        </div>

        <div v-else-if="!notifications.length" class="notif-empty">
          No notifications yet
        </div>

        <div v-else class="notif-list">
          <RouterLink
            v-for="n in notifications"
            :key="n.id"
            :to="n.url || '/'"
            class="notif-item"
            :class="{ 'notif-item--unread': !n.read }"
            @click="markRead(n.id); open = false"
          >
            <div class="notif-dot" v-if="!n.read" />
            <div class="notif-body">
              <strong>{{ n.title }}</strong>
              <p>{{ n.body }}</p>
              <span class="notif-time">{{ timeAgo(n.created_at) }}</span>
            </div>
          </RouterLink>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { getAuthHeaders } from '../../services/api.js'

const API_URL = import.meta.env.VITE_API_URL ?? ''

interface Notification {
  id:         string
  type:       string
  title:      string
  body:       string
  url?:       string
  read:       boolean
  created_at: string
}

const open          = ref(false)
const loading       = ref(false)
const notifications = ref<Notification[]>([])
const unreadCount   = ref(0)
const bellRef       = ref<HTMLElement | null>(null)

async function fetchNotifications() {
  loading.value = true
  try {
    const res  = await fetch(`${API_URL}/api/notifications`, { headers: getAuthHeaders(), credentials: 'include' })
    const data = await res.json()
    if (data.success) {
      notifications.value = data.data.notifications
      unreadCount.value   = data.data.unreadCount
    }
  } finally {
    loading.value = false
  }
}

async function markRead(id: string) {
  await fetch(`${API_URL}/api/notifications/read`, {
    method:      'POST',
    headers:     { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body:        JSON.stringify({ id }),
    credentials: 'include',
  })
  const n = notifications.value.find(n => n.id === id)
  if (n && !n.read) { n.read = true; unreadCount.value = Math.max(0, unreadCount.value - 1) }
}

async function markAllRead() {
  await fetch(`${API_URL}/api/notifications/read`, {
    method:      'POST',
    headers:     { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body:        JSON.stringify({ all: true }),
    credentials: 'include',
  })
  notifications.value.forEach(n => n.read = true)
  unreadCount.value = 0
}

function toggleOpen() {
  open.value = !open.value
  if (open.value) fetchNotifications()
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60000)
  if (mins < 60)   return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24)  return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function handleClickOutside(e: MouseEvent) {
  if (bellRef.value && !bellRef.value.contains(e.target as Node)) open.value = false
}

onMounted(() => {
  fetchNotifications()
  // Poll every 5 minutes for new notifications
  const interval = setInterval(fetchNotifications, 5 * 60 * 1000)
  document.addEventListener('click', handleClickOutside)
  onUnmounted(() => { clearInterval(interval); document.removeEventListener('click', handleClickOutside) })
})
</script>

<style scoped>
.notif-bell { position: relative; }

.bell-btn {
  position: relative; width: 36px; height: 36px; border-radius: 50%;
  border: none; background: transparent; color: var(--ink);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: background 0.15s;
}
.bell-btn:hover { background: var(--c-paper-alt); }

.badge {
  position: absolute; top: 2px; right: 2px;
  min-width: 16px; height: 16px; ;
  background: var(--c-danger); color: var(--ink);
  font-size: 9px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  padding: 0 3px; border: 1.5px solid var(--paper);
}

.notif-dropdown {
  position: absolute; top: calc(100% + 8px); right: 0;
  width: 320px; background: var(--c-white);
  border: 1px solid var(--rule); ;
  
  overflow: hidden; z-index: 100;
}

.notif-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom:1px solid var(--rule-soft);
  font-size: 13px; font-weight: 700;
}
.mark-all-btn {
  font-size: 11px; color: var(--ink-faint); background: none;
  border: none; cursor: pointer; font-weight: 500;
}
.mark-all-btn:hover { color: var(--ink); }

.notif-loading { padding: 24px; text-align: center; }
.notif-empty   { padding: 24px; text-align: center; font-size: 13px; color: var(--ink-faint); }

.notif-list { max-height: 340px; overflow-y: auto; }

.notif-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 16px; text-decoration: none; color: var(--ink);
  border-bottom:1px solid var(--rule-soft);
  transition: background 0.12s;
}
.notif-item:hover         { background: var(--paper); }
.notif-item--unread       { background: rgba(124,29,29,0.06); }
.notif-item:last-child    { border-bottom: none; }

.notif-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--accent-dark); flex-shrink: 0; margin-top: 4px;
}
.notif-body { flex: 1; min-width: 0; }
.notif-body strong { display: block; font-size: 13px; margin-bottom: 2px; }
.notif-body p      { font-size: 12px; color: var(--ink-soft); margin: 0 0 4px; line-height: 1.4; }
.notif-time        { font-size: 11px; color: var(--ink-faint); }

.notif-drop-enter-active,
.notif-drop-leave-active { transition: opacity 0.15s, transform 0.15s; }
.notif-drop-enter-from,
.notif-drop-leave-to     { opacity: 0; transform: translateY(-6px); }
</style>
